import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, MockBanner, StatCard } from "@/components/common";
import { ChartLineUp, Trophy } from "@phosphor-icons/react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const tooltipStyle = { background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };

export default function Analytics() {
  const { data, loading } = useFetch("/analytics");
  if (loading || !data) return <Loading />;

  return (
    <div>
      <PageHeader icon={ChartLineUp} title="Analytics"
        subtitle="Unified analytics across views, retention, languages, characters and formats — populated with real data once YouTube & Meta connect." />
      <MockBanner>{data.note}</MockBanner>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Views" value={(data.demo_views_series || []).length ? "17.9K" : "—"} sub={(data.demo_views_series || []).length ? "last 7 days" : "no connected data yet"} accent="#00E5FF" />
        <StatCard label="Avg retention" value={(data.retention || []).length ? "42%" : "—"} sub={(data.retention || []).length ? "connected data" : "no data yet"} accent="#00FF9D" />
        <StatCard label="Best language" value={data.best.language} accent="#FF007F" />
        <StatCard label="Best character" value={data.best.character} accent="#FFB800" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Views · last 7 days</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.demo_views_series}>
              <defs><linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} /><stop offset="100%" stopColor="#00E5FF" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#52525B" fontSize={12} /><YAxis stroke="#52525B" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="views" stroke="#00E5FF" strokeWidth={2} fill="url(#v)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Audience retention</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.retention}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="point" stroke="#52525B" fontSize={11} /><YAxis stroke="#52525B" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="pct" stroke="#00FF9D" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Views by language</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.language_perf}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="language" stroke="#52525B" fontSize={10} /><YAxis stroke="#52525B" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="views" fill="#FF007F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Character retention</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.character_perf}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="character" stroke="#52525B" fontSize={12} /><YAxis stroke="#52525B" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="retention" fill="#FFB800" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel>
        <div className="flex items-center gap-2 mb-4"><Trophy size={18} weight="duotone" className="text-[#FFB800]" /><span className="font-display font-bold text-white">Best Performers</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(data.best).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/8 bg-black/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, " ")}</div>
              <div className="text-sm text-white font-medium mt-1">{v}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
